/**
 * Normalizes Asset Manager pack-index JSON to imagemap shape:
 * `Record<`${masterId}_${colorCode}`, Array<{ imageurl: string }>>`.
 */
export interface ImageMapEntry {
    imageurl: string;
}

export type SeasonImageMap = Record<string, ImageMapEntry[]>;

function firstNonEmptyString(...cands: unknown[]): string | undefined {
    for (let i = 0; i < cands.length; i++) {
        const value = cands[i];
        if (typeof value === 'string' && value.trim().length) {
            return value.trim();
        }
    }
    return undefined;
}

function rowFromImageObject(img: unknown): ImageMapEntry | null {
    if (!img || typeof img !== 'object') {
        return null;
    }

    const o = img as Record<string, unknown>;
    const url = firstNonEmptyString(o.imageurl, o.ImageUrl, o.niceUrl, o.NiceUrl, o.url, o.Url);
    return url ? { imageurl: url } : null;
}

function variantKey(style: string, color: string): string {
    return String(style) + '_' + String(color);
}

function sortOrderFromImage(img: Record<string, unknown>): number {
    const value = img.sort_order !== undefined ? img.sort_order : img.SortOrder;
    if (typeof value === 'number' && !isNaN(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
}

function normalizeLegacyLikeMap(obj: Record<string, unknown>): SeasonImageMap {
    const out: SeasonImageMap = {};
    const keys = Object.keys(obj);

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const value = obj[key];
        if (!Array.isArray(value)) {
            continue;
        }

        const rows: ImageMapEntry[] = [];
        for (let j = 0; j < value.length; j++) {
            const row = rowFromImageObject(value[j]);
            if (row) {
                rows.push(row);
            }
        }

        if (rows.length) {
            out[key] = rows;
        }
    }

    return out;
}

function normalizeAssetManagerEntriesIndex(obj: Record<string, unknown>): SeasonImageMap | null {
    const entries = obj.entries !== undefined ? obj.entries : obj.Entries;
    if (!Array.isArray(entries) || entries.length === 0) {
        return null;
    }

    const out: SeasonImageMap = {};
    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        if (!entry || typeof entry !== 'object') {
            continue;
        }

        const ent = entry as Record<string, unknown>;
        const style = firstNonEmptyString(ent.style_code, ent.StyleCode, ent.style, ent.Style, ent.masterId, ent.MasterId);
        const color = firstNonEmptyString(ent.color_code, ent.ColorCode, ent.color, ent.Color, ent.colorCode);
        const season = firstNonEmptyString(ent.season, ent.Season);

        if (!style || !color || !season) {
            continue;
        }

        let images = ent.images !== undefined ? ent.images : ent.Images !== undefined ? ent.Images : undefined;
        if ((!Array.isArray(images) || images.length === 0) && ent.primary_image) {
            images = [ent.primary_image];
        }
        if (!Array.isArray(images) || images.length === 0) {
            continue;
        }

        const sorted = images.slice().sort((a, b) => {
            if (!a || typeof a !== 'object') return 1;
            if (!b || typeof b !== 'object') return -1;
            return sortOrderFromImage(a as Record<string, unknown>) - sortOrderFromImage(b as Record<string, unknown>);
        });

        const rows: ImageMapEntry[] = [];
        for (let j = 0; j < sorted.length; j++) {
            const img = sorted[j];
            if (!img || typeof img !== 'object') {
                continue;
            }

            const image = img as Record<string, unknown>;
            const filename = firstNonEmptyString(image.filename, image.Filename);
            const directUrl = rowFromImageObject(img);

            if (filename) {
                rows.push({ imageurl: '/' + season + '/' + filename.replace(/^\/+/, '') });
            } else if (directUrl) {
                rows.push(directUrl);
            }
        }

        if (rows.length) {
            out[variantKey(style, color)] = rows;
        }
    }

    return Object.keys(out).length ? out : null;
}

function flattenVariantsArray(arr: unknown[]): SeasonImageMap {
    const out: SeasonImageMap = {};

    for (let i = 0; i < arr.length; i++) {
        const item = arr[i];
        if (!item || typeof item !== 'object') {
            continue;
        }

        const obj = item as Record<string, unknown>;
        const style = firstNonEmptyString(obj.style_code, obj.StyleCode, obj.style, obj.Style, obj.masterId, obj.MasterId);
        const color = firstNonEmptyString(obj.color_code, obj.ColorCode, obj.color, obj.Color, obj.colorCode);
        const images = obj.images !== undefined ? obj.images : obj.Images;

        if (!style || !color || !Array.isArray(images)) {
            continue;
        }

        const rows: ImageMapEntry[] = [];
        for (let j = 0; j < images.length; j++) {
            const row = rowFromImageObject(images[j]);
            if (row) {
                rows.push(row);
            }
        }

        if (rows.length) {
            out[variantKey(style, color)] = rows;
        }
    }

    return out;
}

function unwrapRoot(raw: unknown): unknown {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return raw;
    }

    const root = raw as Record<string, unknown>;
    return root.data !== undefined
        ? root.data
        : root.Data !== undefined
          ? root.Data
          : root.index !== undefined
            ? root.index
            : root.Index !== undefined
              ? root.Index
              : raw;
}

export function normalizePacksIndexToImageMap(raw: unknown): SeasonImageMap {
    if (raw == null) {
        return {};
    }

    let root = unwrapRoot(raw);
    if (typeof root === 'string') {
        try {
            root = unwrapRoot(JSON.parse(root));
        } catch (_e) {
            return {};
        }
    }

    if (Array.isArray(root)) {
        return flattenVariantsArray(root);
    }

    if (typeof root === 'object' && root !== null) {
        const obj = root as Record<string, unknown>;
        const fromEntries = normalizeAssetManagerEntriesIndex(obj);
        if (fromEntries) {
            return fromEntries;
        }

        if (Array.isArray(obj.variants)) {
            return flattenVariantsArray(obj.variants);
        }
        if (Array.isArray(obj.items)) {
            return flattenVariantsArray(obj.items);
        }
        if (Array.isArray(obj.Items)) {
            return flattenVariantsArray(obj.Items);
        }

        const flattened = flattenVariantsArray([obj]);
        return Object.keys(flattened).length ? flattened : normalizeLegacyLikeMap(obj);
    }

    return {};
}
